import { Component, OnDestroy, OnInit } from '@angular/core';
import { NzLayoutModule } from 'ng-zorro-antd/layout';
import { NzCardModule } from 'ng-zorro-antd/card';
import { NzBadgeModule } from 'ng-zorro-antd/badge';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { ProvisionRequestApiService } from '../api/provision-request-api.service';
import { DateTime } from 'luxon';
import { firstValueFrom } from 'rxjs';
import { Router } from '@angular/router';
import { NzTagModule } from 'ng-zorro-antd/tag';
import { NzListModule } from 'ng-zorro-antd/list';
import { NzModalModule } from 'ng-zorro-antd/modal';
import { NzSpinComponent } from 'ng-zorro-antd/spin';
import { NzSelectModule } from 'ng-zorro-antd/select';

import { GetPersonOutput } from '../api/person-api.types';
import { PersonApiService } from '../api/person-api.service';
import { CommonModule } from '@angular/common';
import {
  CdkFixedSizeVirtualScroll,
  CdkVirtualForOf,
  CdkVirtualScrollViewport,
} from '@angular/cdk/scrolling';
import { ClothingApiService } from '../api/clothing-api.service';
import { NzMessageService } from 'ng-zorro-antd/message';
import { GetClothingOutput } from '../api/clothing-api.types';
import { ScannerService } from '../shared/services/scanner.service';
import { NzModalService } from 'ng-zorro-antd/modal';
import { FormsModule } from '@angular/forms';

@Component({
  selector: 'app-home',
  templateUrl: './home.component.html',
  styleUrls: ['./home.component.scss'],
  standalone: true,
  imports: [
    NzLayoutModule,
    NzCardModule,
    NzBadgeModule,
    NzIconModule,
    NzTagModule,
    NzListModule,
    CommonModule,
    CdkVirtualScrollViewport,
    CdkFixedSizeVirtualScroll,
    CdkVirtualForOf,
    NzModalModule,
    NzButtonModule,
    NzSpinComponent,
    NzSelectModule,
    FormsModule,
  ],
})
export class HomeComponent implements OnInit, OnDestroy {
  protected readonly DateTime = DateTime;
  endingRequestsAmount = 0;
  startingRequestsAmount = 0;
  expiredRequestsAmount = 0;

  secondKeyPersons: GetPersonOutput[] = [];
  wornPairOfShoes: GetClothingOutput[] = [];
  keyLoading = true;
  shoesLoading = true;

  // Scanner variables
  scannerModalVisible = false;
  scannerFlag: number | null = null;
  scannedCodeBuffer = '';
  scanTimeout: any;
  modalTitle = '';
  scanStartTime = 0;
  scanSpeedThreshold = 50; // milliseconds per character
  private boundKeyDownHandler = this.onKeyDown.bind(this);
  private errorSubscription: any;

  constructor(
    private requestApiService: ProvisionRequestApiService,
    private personApiService: PersonApiService,
    private clothingApiService: ClothingApiService,
    private messageService: NzMessageService,
    private scannerService: ScannerService,
    private router: Router,
    private modal: NzModalService,
  ) {}

  async ngOnInit() {
    window.addEventListener('keydown', this.boundKeyDownHandler);
    await this.getFilteredProvisionRequests();
    await this.getPersonsWithSecondKeys();
    await this.getPersonsWithRentedShoes();
    this.errorSubscription = this.scannerService.errorSubject.subscribe(
      (message) => this.showBlockingModal(message),
    );
  }

  ngOnDestroy() {
    window.removeEventListener('keydown', this.boundKeyDownHandler);
    this.errorSubscription?.unsubscribe();
  }

  async onKeyDown(event: KeyboardEvent) {
    if (!this.scannerModalVisible) {
      return;
    }
    if (event.key.length > 1) {
      return;
    }

    const currentTime = DateTime.now().toMillis();
    if (this.scannedCodeBuffer === '') {
      this.scanStartTime = currentTime;
    }

    this.scannedCodeBuffer += event.key;

    clearTimeout(this.scanTimeout);
    this.scanTimeout = setTimeout(async () => {
      const scannedCode = this.scannedCodeBuffer.trim();
      this.scannedCodeBuffer = '';

      if (scannedCode) {
        const scanDuration = DateTime.now().toMillis() - this.scanStartTime;
        const avgCharTime = scanDuration / scannedCode.length;

        if (avgCharTime < this.scanSpeedThreshold) {
          if (this.scannerFlag === 0) {
            await this.scannerService.scanPerson(scannedCode);
          } else if (this.scannerFlag === 1) {
            await this.scannerService.massScanWithdrawal(scannedCode);
          } else if (this.scannerFlag === 2) {
            await this.scannerService.massScanLaundryWithdrawal(scannedCode);
          }
        }
      }
    }, 100);
  }

  onClickProvisionRequests(status: string) {
    let provisionParams = {};
    const fiveDaysAhead = DateTime.now().plus({ days: 5 }).toISODate();

    if (status === 'ending') {
      provisionParams = {
        datum_bis_start: DateTime.now().toISODate(),
        datum_bis_ende: fiveDaysAhead,
      };
    } else if (status === 'starting') {
      provisionParams = {
        datum_von_start: DateTime.now().toISODate(),
        datum_von_ende: fiveDaysAhead,
      };
    } else if (status === 'expired') {
      provisionParams = {
        datum_bis_ende: DateTime.now().toISODate(),
      };
    }
    this.router.navigate(['provisions'], { queryParams: provisionParams });
  }

  nagivateToLocker(id: number) {
    this.router.navigate(['lockers', id]);
  }

  navigateToPerson(id: number) {
    this.router.navigate(['persons', id]);
  }

  async getPersonsWithSecondKeys() {
    const persons = await firstValueFrom(
      this.personApiService.searchPerson({ hat_spind_zugewiesen: true }),
    );

    this.secondKeyPersons = persons.filter((person) =>
      person.spinde.find(
        (locker) => locker.zweitschluessel_ausgegeben_am !== null,
      ),
    );
    this.keyLoading = false;
  }

  async getPersonsWithRentedShoes() {
    const rentedShoes = await firstValueFrom(
      this.clothingApiService.getAllClothing({ typ: 1 }),
    );

    this.wornPairOfShoes = rentedShoes.filter((shoes) => shoes.person !== null);
    this.shoesLoading = false;
  }

  async getFilteredProvisionRequests() {
    const fiveDaysAhead = DateTime.now().plus({ days: 5 }).toISODate();

    try {
      const startingRequests = await firstValueFrom(
        this.requestApiService.getAllProvisionRequests({
          datum_von_start: DateTime.now().toISODate(),
          datum_von_ende: fiveDaysAhead,
        }),
      );
      this.startingRequestsAmount = startingRequests.filter(
        (request) => request.beendet_am === null,
      ).length;
    } catch {
      this.startingRequestsAmount = 0;
    }

    try {
      const endingRequests = await firstValueFrom(
        this.requestApiService.getAllProvisionRequests({
          datum_bis_ende: fiveDaysAhead,
          datum_bis_start: DateTime.now().toISODate(),
        }),
      );
      this.endingRequestsAmount = endingRequests.filter(
        (request) => request.beendet_am === null,
      ).length;
    } catch {
      this.endingRequestsAmount = 0;
    }

    try {
      const expiredRequests = await firstValueFrom(
        this.requestApiService.getAllProvisionRequests({
          datum_bis_ende: DateTime.now().toISODate(),
        }),
      );
      this.expiredRequestsAmount = expiredRequests.filter(
        (request) => request.beendet_am === null,
      ).length;
    } catch {
      this.expiredRequestsAmount = 0;
    }
  }

  openScannerModal(flag: number, title: string): void {
    this.modalTitle = title;
    // 0 = Person, 1 = Clothing
    this.scannerFlag = flag;
    this.scannerModalVisible = true;
  }

  closeScannerModel(): void {
    this.scannerModalVisible = false;
  }

  showBlockingModal(message: string) {
    this.scannerModalVisible = false;
    this.modal.error({
      nzTitle: 'Fehler beim Scan',
      nzContent: message,
      nzOkText: 'Bestätigen',
      nzClosable: false,
      nzMaskClosable: false,
      nzOnOk: () => {
        this.scannerModalVisible = true;
      },
    });
  }
}
